/**
 * Social in-app browsers (Instagram, Facebook, TikTok, …) ship a WebView
 * without a download manager. Navigating to a `Content-Disposition: attachment`
 * vCard replaces the profile with a blank page. Named apps are matched before
 * the generic Android WebView token.
 */
const IN_APP_BROWSER_MATCHERS: ReadonlyArray<{
  id: InAppBrowserId;
  pattern: RegExp;
}> = [
  { id: 'instagram', pattern: /Instagram/i },
  {
    id: 'messenger',
    pattern: /MessengerForiOS|FB_IAB\/MESSENGER|FBAN\/Messenger/i,
  },
  { id: 'facebook', pattern: /FBAN|FBAV|FB_IAB|FB4A/i },
  { id: 'tiktok', pattern: /TikTok|BytedanceWebview|musical_ly|trill_/i },
  { id: 'threads', pattern: /\bBarcelona\b/ },
  { id: 'linkedin', pattern: /LinkedInApp/i },
  { id: 'snapchat', pattern: /\bSnapchat\b/i },
  { id: 'twitter', pattern: /\bTwitter\b/ },
  { id: 'pinterest', pattern: /\bPinterest\b/i },
  { id: 'line', pattern: /\bLine\// },
  { id: 'wechat', pattern: /MicroMessenger/i },
  { id: 'webview', pattern: /;\s*wv\)/ },
];

const IN_APP_BROWSER_LABELS: Record<
  Exclude<InAppBrowserId, 'webview'>,
  string
> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  messenger: 'Messenger',
  tiktok: 'TikTok',
  threads: 'Threads',
  linkedin: 'LinkedIn',
  snapchat: 'Snapchat',
  twitter: 'X',
  pinterest: 'Pinterest',
  line: 'LINE',
  wechat: 'WeChat',
};

export type InAppBrowserId =
  | 'instagram'
  | 'facebook'
  | 'messenger'
  | 'tiktok'
  | 'threads'
  | 'linkedin'
  | 'snapchat'
  | 'twitter'
  | 'pinterest'
  | 'line'
  | 'wechat'
  | 'webview';

export type ContactSavePlatform = 'ios' | 'android' | 'other';

export type ContactSavePlan =
  | { kind: 'browser' }
  | {
      kind: 'in-app';
      platform: ContactSavePlatform;
      app: InAppBrowserId;
      /** Brand name. Null for unnamed WebViews — use translated “this app”. */
      appLabel: string | null;
    };

export function detectInAppBrowser(userAgent: string): InAppBrowserId | null {
  for (const matcher of IN_APP_BROWSER_MATCHERS) {
    if (matcher.pattern.test(userAgent)) return matcher.id;
  }
  return null;
}

export function detectContactSavePlatform(
  userAgent: string,
): ContactSavePlatform {
  if (/iPad|iPhone|iPod/.test(userAgent)) return 'ios';
  if (/Macintosh/.test(userAgent) && /Mobile/.test(userAgent)) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  return 'other';
}

export function resolveContactSavePlan(userAgent: string): ContactSavePlan {
  const app = detectInAppBrowser(userAgent);
  if (!app) return { kind: 'browser' };
  return {
    kind: 'in-app',
    platform: detectContactSavePlatform(userAgent),
    app,
    appLabel: app === 'webview' ? null : IN_APP_BROWSER_LABELS[app],
  };
}

/**
 * Android VIEW intent that leaves a WebView for the default browser.
 * Hash and semicolons are escaped so they don't break the `#Intent` syntax.
 */
export function buildAndroidExternalBrowserIntent(
  pageUrl: string,
): string | null {
  let url: URL;
  try {
    url = new URL(pageUrl);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  const scheme = url.protocol.slice(0, -1);
  const path = `${url.pathname}${url.search}`.replace(/;/g, '%3B');
  const hash = url.hash ? `%23${encodeURIComponent(url.hash.slice(1))}` : '';
  return `intent://${url.host}${path}${hash}#Intent;scheme=${scheme};action=android.intent.action.VIEW;end`;
}
